<template>
  <div class="product-buy-section-container container">
    <div class="product-buy-section">
      <div class="product-buy-images" v-intersection="flashChevrons">
        <div class="product-buy-image" ref="productImage">
          <!-- <transition name="product-image-fade" mode="out-in"> -->
          <PreserveRatio :width="2890" :height="1500" :key="media.url">
            <div v-if="media && media.type === 'IMAGE'" class="product-buy-image-src" :key="media.url" @click="openGallery">
              <ResponsiveImage
                :url="`${media.url}?fit=max&w={width}&q={q}`"
                :lazy="false"
                :widths="[768, 930, 1050, 1370, 1370]"
                :breakpoints="[320, 768, 970, 1400, 1600]"
                :alt="`${product.title} - Primary View ${currentImageIndex} of ${gallerySize} (Open Fullscreen View).`"
                @mousemove="flashChevrons"
              ></ResponsiveImage>
            </div>
            <VideoPlayer
              v-else-if="mounted && media && media.type === 'VIDEO'"
              :key="media.url"
              :auto-load="true"
              :active="true"
              :poster="media.thumbnailUrl"
              :hls="media.hlsUrl"
              :dash="media.dashUrl"
              :mp4="media.url"
              :product="product"
            ></VideoPlayer>
            <transition name="fade">
              <div class="product-buy-image-left" @click="swipeRight" v-if="showChevrons">
                <button class="button white filled icon" aria-label="Previous gallery image" @click="flashChevrons">
                  <span class="af af-chevron-left"></span>
                </button>
              </div>
            </transition>

            <transition name="fade">
              <div class="product-buy-image-right" @click="swipeLeft" v-if="showChevrons">
                <button class="button white filled icon" aria-label="Next gallery image" @click="flashChevrons">
                  <span class="af af-chevron-right"></span>
                </button>
              </div>
            </transition>
            <ProductImageControls
              v-if="media && media.type === 'IMAGE'"
              :product="product"
              @zoom="openGallery"
              class="product-buy-image-controls"
            ></ProductImageControls>
          </PreserveRatio>
          <!-- </transition> -->
        </div>
        <ProductThumbnails
          :product="product"
          :active-media="activeMedia"
          @clickThumbnail="updateActiveMedia"
          ref="productThumbnails"
        ></ProductThumbnails>
      </div>

      <div
        v-if="isProductAvailableInGeolocation && isARButtonEnabled && mounted"
        :key="`product-viewer-button-${product.hashKey}-buy-section`"
        class="product-viewer-button-mobile-buy-section"
      >
        <ProductViewerButton :product="product" />
      </div>
      <div class="product-buy-information">
        <div class="product-buy-configurator">
          <ProductConfigurator
            :configurations="configurations"
            :configurations-length="configurations ? configurations.length : 0"
            @update:modelValue="onConfiguratorChanged"
          />
          <div v-if="loading || isDeliveryEstimateLoading" class="product-spinner">
            <slot name="loader">
              <span class="loader tiny"></span>
            </slot>
          </div>
        </div>
        <div v-if="!product?.isEtaAtRisk" ref="atcSection" class="product-atc-container">
          <AddToCartButton
            v-if="isProductAvailableInGeolocation"
            ref="atcRef"
            :scroll-first="false"
            :products="productToCartFormat"
            @added="onAddToCart"
            :isEtaAtRisk="product?.isEtaAtRisk"
          />
          <ProductFavorite
            v-if="!isProductAvailableInGeolocation"
            :product="product"
            :show-favorite="true"
            :show-favorite-text="true"
            class="border"
            :class="{
              'product-unavailable-favorite-button': !isProductAvailableInGeolocation,
            }"
            source="product-page"
            @onAdd="onFavoriteAdded"
          />
          <div v-else class="product-available-favorite-button">
            <ProductFavorite :product="product" :show-favorite="true" class="border" source="product-page" @onAdd="onFavoriteAdded" />
          </div>
        </div>
        <Divider v-if="configurations && configurations.length"></Divider>
        <div class="product-buy-details" v-if="product && product.status === 'AVAILABLE'" :class="[product?.isEtaAtRisk && 'notify-container']">
          <ProductDelivery
            v-if="!product?.isEtaAtRisk"
            :loading="isDeliveryEstimateLoading"
            :product="product"
            :price="price"
            :error="deliveryEstimateError"
            :accurate="accurate"
            :location="location"
            :estimate-message="estimateMessage"
            @updateZip="updateZip"
          />
          <ProductRiskyETAWarning
            v-if="product?.isEtaAtRisk && isProductAvailableInGeolocation"
            :messageSlug="'pdp-risky-eta-text'"
            :defaultMessage="'Sorry, this item is currently out of stock.'"
            :navigationCategory="product?.navigationCategory && product?.navigationCategory?.length ? product?.navigationCategory[0] : null"
            :product-id="product?.hashKey"
            :currency-code="price?.currencyCode"
            :show-on-product-page="true"
          />
          <ProductStock
            v-if="isProductAvailableInGeolocation && !product?.isEtaAtRisk"
            :loading="isDeliveryEstimateLoading"
            :product="product"
            :error="deliveryEstimateError"
            :estimate="estimate"
          />
          <ProductFinancing v-if="isProductAvailableInGeolocation && !product?.isEtaAtRisk" :amount-in-cents="price.final" />
        </div>

        <ProductTacticalCrossSell v-if="isProductAvailableInGeolocation" :slug="`product-${product.hashKey}`" :key="product.hashKey" />

        <div v-if="product?.isEtaAtRisk" ref="atcSection" class="product-atc-container atc-margin">
          <AddToCartButton
            v-if="isProductAvailableInGeolocation"
            ref="atcRef"
            :scroll-first="false"
            :products="productToCartFormat"
            @added="onAddToCart"
            :isEtaAtRisk="product?.isEtaAtRisk"
          />
          <ProductFavorite
            v-if="!isProductAvailableInGeolocation"
            :product="product"
            :show-favorite="true"
            :show-favorite-text="true"
            class="border"
            :class="{
              'product-unavailable-favorite-button': !isProductAvailableInGeolocation,
            }"
            source="product-page"
            @onAdd="onFavoriteAdded"
          />
          <div v-else class="product-available-favorite-button">
            <ProductFavorite :product="product" :show-favorite="true" class="border" source="product-page" @onAdd="onFavoriteAdded" />
          </div>
        </div>
        <ProductUnavailableMessage
          :isProductAvailable="isProductAvailableInGeolocation"
          :isUpdatedGallery="true"
          :navigationCategory="product?.navigationCategory?.[0]"
        />
      </div>
    </div>
    <Divider margin-top="var(--space-10)" margin-bottom="var(--space-10)" />
  </div>
</template>

<script>
import { onMounted, ref, computed } from 'vue';
import { Divider, Swipe } from '@/pixel/index';
import { throttle } from '@/components/commons/MiniLodash';
import ResponsiveImage from '@/components/commons/ResponsiveImage.vue';
import PreserveRatio from '@/components/commons/PreserveRatio.vue';
import AddToCartButton from '@/components/commons/AddToCartButton.vue';
import ProductFavorite from '@/components/product/ProductFavorite.vue';
import ProductUnavailableMessage from '@/components/product/ProductUnavailableMessage.vue';
import LocationService from '@/services/LocationService.js';
import useDeliveryEstimate from '@/compositions/useDeliveryEstimate.js';
import ProductConfigurator from '@/components/product/ProductConfigurator.vue';
import ProductFinancing from '@/components/product/ProductFinancing.vue';
import ProductDelivery from '@/components/product/ProductDelivery.vue';
import ProductStock from '@/components/product/ProductStock.vue';
import ProductThumbnails from '@/components/product/ProductThumbnails.vue';
import Tracking from '@/components/commons/Tracking.js';
import useCommon from '@/compositions/useCommon';
import useLifecycleHook from '@/compositions/useLifecycleHook.js';
import useExperiment from '@/compositions/useExperiment.js';
import ZoomIcon from '@/assets/product/zoom-in.svg';
import VideoPlayer from '@/components/product/Video.vue';
import ProductViewerButton from '@/components/product/ProductViewerButton.vue';
import ProductImageControls from '@/components/product/ProductImageControls.vue';
import ProductRiskyETAWarning from '@/components/commons/ProductRiskyETAWarning.vue';
import Intersection from '@/components/commons/Intersection.js';
import ProductTacticalCrossSell from '@/components/product/ProductTacticalCrossSell.vue';

export default {
  name: 'ProductBuySection',
  components: {
    PreserveRatio,
    ProductConfigurator,
    ProductFinancing,
    ProductDelivery,
    ProductStock,
    Divider,
    ProductThumbnails,
    ResponsiveImage,
    ZoomIcon,
    VideoPlayer,
    ProductUnavailableMessage,
    ProductImageControls,
    ProductViewerButton,
    AddToCartButton,
    ProductFavorite,
    ProductRiskyETAWarning,
    ProductTacticalCrossSell,
  },
  props: {
    checkProductBuySectionATCInView: Function,
    productToCartFormat: {
      type: Array,
      required: true,
    },
    activeMedia: {
      default: 0,
      type: Number,
    },
    product: {
      type: Object,
      default: null,
    },
    price: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    currentNavigationCategory: {},
    configurations: {},
    isAddToCartVisibleOnPageNav: Boolean,
  },
  emits: ['updateActiveMedia', 'openGallery', 'configuratorChanged', 'disableAtcPageNav', 'addToCart'],
  directives: {
    intersection: Intersection,
  },
  setup(props) {
    const { mounted } = useLifecycleHook();
    const { isFeatureVariant } = useExperiment();
    const checkElementInViewRef = ref(null);
    const {
      loading: isDeliveryEstimateLoading,
      error: deliveryEstimateError,
      accurate,
      location,
      estimate,
      estimateMessage,
      loadgql: loadDeliveryEstimate,
    } = useDeliveryEstimate();
    const productThumbnails = ref(null);
    const productImage = ref(null);
    const scrollCallback = ref(null);
    const { loadMessaging } = useCommon();
    const isARButtonEnabled = computed(() => isFeatureVariant('ar-button', 'on'));
    const gallerySize = computed(() => props.product.media.length);
    const currentImageIndex = computed(() => {
      const imageIndex = props.activeMedia || 0;
      return imageIndex + 1;
    });
    const showChevrons = ref(false);
    let timer;

    onMounted(() => {
      if (props.product) {
        loadDeliveryEstimate({
          product: props.product.hashKey,
          zip: LocationService.getPreferredLocation().zipcode,
        });
      }
    });

    const resetTimer = () => {
      clearTimeout(timer);
    };

    const flashChevrons = () => {
      resetTimer();
      showChevrons.value = true;
      timer = window.setTimeout(() => {
        showChevrons.value = false;
      }, 3000);
    };
    const media = computed(() => {
      const index = props.activeMedia;
      if (props.product && props.product.media) {
        return props.product.media[index] || {};
      }
      return {};
    });

    const mediaLength = computed(() => {
      if (props.product && props.product.media) {
        return props.product.media.length;
      }
      return 0;
    });

    return {
      isDeliveryEstimateLoading,
      deliveryEstimateError,
      accurate,
      location,
      estimate,
      estimateMessage,
      loadDeliveryEstimate,
      mounted,
      loadMessaging,
      media,
      mediaLength,
      productThumbnails,
      productImage,
      isARButtonEnabled,
      scrollCallback,
      checkElementInViewRef,
      gallerySize,
      currentImageIndex,
      showChevrons,
      flashChevrons,
      resetTimer,
    };
  },
  beforeMount() {
    window.addEventListener('keydown', this.handleKeydown);

    if (!import.meta.env.SSR) {
      this.swipe = new Swipe();
      this.swipe.setOnLeft(() => this.swipeLeft());
      this.swipe.setOnRight(() => this.swipeRight());
    }
  },
  mounted() {
    if (this.productImage) {
      this.swipe.attachListeners(this.productImage);
    }
    this.initializeScrollListener();
  },
  beforeUnmount() {
    if (this.productImage) {
      this.swipe.detachListeners(this.productImage);
    }
    window.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('scroll', this.scrollCallback);
  },
  computed: {
    isProductAvailableInGeolocation() {
      return this.product && this.product.isProductAvailableInGeo;
    },
  },
  methods: {
    initializeScrollListener() {
      /**
       * If the add to cart container section or the full width favorite button (unavailable products) is visible, then set up the scroll listener
       * to check if it is in view.
       */
      const throttleCheckAddToCartInView = throttle((el) => this.checkProductBuySectionATCInView(el), 100);
      this.scrollCallback = (e) => throttleCheckAddToCartInView(this.$refs.atcSection);
      window.addEventListener('scroll', this.scrollCallback);
      window.addEventListener('resize', (e) => {
        /**
         * Handles a rare edge case, where the customer is resizes the screen and when we are under the mobile breakpoint
         * we want to attach the listener on scroll since the ATC button or the Favorite Button (On Unavailable Products) would now be visible
         *
         * Also if the ATC button/ Favorite Button on the Product Nav were visible, as we move above the mobile breakpoint
         * we want to hide it and remove the scroll listener as well
         */
        if (this.$refs.atcSection && window.getComputedStyle(this.$refs.atcSection).display !== 'none') {
          this.checkProductBuySectionATCInView(this.$refs.atcSection);
          window.addEventListener('scroll', this.scrollCallback);
        } else {
          if (this.isAddToCartVisibleOnPageNav) {
            this.disableAtcOnProductPageNav();
          }
          window.removeEventListener('scroll', this.scrollCallback);
        }
      });
    },
    onAddToCart(products) {
      this.$emit('addToCart', products);
      Tracking.track('PDP_Click_Floating_Nav', {
        element: 'add to cart',
        productId: products[0].id,
      });
    },
    onFavoriteAdded(productId) {
      Tracking.track('PDP_Click_Floating_Nav', { element: 'favorite', productId });
    },
    disableAtcOnProductPageNav() {
      this.$emit('disableAtcPageNav');
    },
    updateZip(zipCode) {
      if (this.product) {
        this.loadDeliveryEstimate({
          product: this.product.hashKey,
          zip: zipCode,
        });

        Tracking.track('PDP Zip Updated', {
          productId: this.product.hashKey,
          zip: zipCode,
        });
      }
      this.loadMessaging(zipCode);
    },
    swipeLeft() {
      this.updateActiveMedia(this.activeMedia < this.mediaLength - 1 ? this.activeMedia + 1 : 0);
    },
    swipeRight() {
      this.updateActiveMedia(this.activeMedia > 0 ? this.activeMedia - 1 : this.mediaLength - 1);
    },
    handleKeydown(e) {
      // verifies if the product details gallery is open to not conflict with the gallery key down addEventListener.
      const isGalleryOpen = window.location.search.includes('gallery');
      // verifies the element to prevent the swipe from occurring when inside an input.
      const isTargetInput = e.target.tagName === 'INPUT';

      if (!isGalleryOpen && !isTargetInput) {
        switch (e.key) {
          case 'ArrowLeft':
            this.swipeRight();
            break;
          case 'ArrowRight':
            this.swipeLeft();
            break;
          case '+':
          case '=':
            this.openGallery();
            break;
        }
      }
    },
    redirectToProductPageReviews() {
      window.location.href = `${this.product.productUrl}?pr=true`;
    },
    onConfiguratorChanged(config) {
      this.$emit('configuratorChanged', config);
    },
    openGallery() {
      this.$emit('openGallery');
      Tracking.track('PDP Expand Product Image', {
        productId: this.product.hashKey,
        index: this.activeMedia,
      });
    },

    updateActiveMedia(index) {
      this.$emit('updateActiveMedia', index);
      if (this.productThumbnails) {
        this.productThumbnails.scrollToThumbnail(index);
      }
    },
  },
  watch: {
    'product.hashKey': {
      handler(hashKey) {
        this.loadDeliveryEstimate({
          product: hashKey,
          zip: LocationService.getPreferredLocation().zipcode,
        });
      },
    },
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';

.product-buy-section {
  display: flex;
  width: 100%;
  flex-direction: column;

  @media (min-width: $desktop-breakpoint) {
    flex-direction: row;
  }
}

.product-buy-images {
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: auto;
  min-width: 280px;

  @media (max-width: calc($tablet-breakpoint - 1px)) {
    margin-left: calc(-1 * var(--space-5));
    margin-right: calc(-1 * var(--space-5));
  }
}

.product-buy-image {
  position: relative;

  &:hover {
    .product-buy-image-left,
    .product-buy-image-right {
      opacity: 1;
    }
  }
}

.product-buy-image-src {
  cursor: zoom-in;
}

.product-configuration-group-container:last-of-type {
  @media (max-width: calc($mobile-breakpoint - 1px)) {
    padding-bottom: var(--space-6);
    margin-bottom: 0px;
  }
}

.product-buy-information {
  flex-grow: 0;
  flex-shrink: 0;
  margin-top: var(--space-10);

  @media (max-width: calc($mobile-breakpoint - 1px)) {
    .pixel-divider {
      display: none;
    }
  }

  @media (min-width: $desktop-breakpoint) {
    margin-left: var(--space-10);
    margin-top: 0;
    flex-basis: 355px;
    overflow: inherit;
  }

  @media (min-width: $large-breakpoint) {
    flex-basis: 430px;
  }
}
.product-buy-configurator {
  display: flex;
}

.product-atc-container {
  @media (min-width: $mobile-breakpoint) {
    display: none;
  }
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  .add-to-cart-container {
    width: 100%;
    margin-right: 16px;
    button {
      width: 100%;
    }
  }

  .product-unavailable-favorite-button {
    @media (min-width: $mobile-breakpoint) {
      display: none;
    }
    width: 100%;
    height: 40px;
    position: static;
    box-sizing: border-box;
    display: flex;

    .af {
      margin-right: 10px;
      font-size: 1em;
    }

    &.border {
      right: 20px;
      box-sizing: border-box;
      border-radius: 20px;
      justify-content: center;
      color: var(--grey-medium);
      border: 1px solid var(--grey-light);
    }

    .text {
      display: block;
      font-weight: 600;
      font-size: 16px;
      line-height: 100%;
      order: 2;
      letter-spacing: 1.4px;
      text-transform: uppercase;
      color: var(--grey-medium);
    }
  }

  .product-available-favorite-button {
    .border {
      right: 20px;
      box-sizing: border-box;
      border-radius: 20px;
      width: 40px;
      height: 40px;
      justify-content: center;
      color: var(--grey-medium);
      border: 1px solid var(--grey-light);
    }
    .favorite {
      position: static;
    }
    .af {
      font-size: 1em;
    }
  }
}
.atc-margin {
  margin-top: var(--space-4);
}

.product-buy-details {
  display: block;
  margin-bottom: var(--space-5);

  .product-risky-eta-message {
    text-align: left;
    @media (max-width: calc($desktop-breakpoint - 1px)) {
      text-align: center;
      width: 100%;
    }
  }

  & > * {
    padding-left: 0;
    padding-right: 0;
    margin-top: var(--space-4);
  }

  @media (min-width: $mobile-breakpoint) {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;

    & > * {
      padding-left: var(--space-2);
      padding-right: var(--space-2);
      margin-top: var(--space-4);
    }

    & > *:last-child {
      border: none;
    }
  }

  @media (min-width: $desktop-breakpoint) {
    display: block;

    & > * {
      padding-left: 0;
      padding-right: 0;
      border: none;
      margin-top: var(--space-2);
    }
  }
}
.product-buy-details-flex {
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  margin-top: 0px;
  @media screen and (min-width: $mobile-breakpoint) and (max-width: calc($desktop-breakpoint - 1px)) {
    margin-top: var(--space-10);
  }

  .product-configuration {
    margin-left: 0px;
    .product-configuration-group-container:last-child {
      margin-bottom: 0px;
    }
  }
}

.product-buy-image-left,
.product-buy-image-right {
  position: absolute;
  top: 80px;
  bottom: 80px;
  display: flex;
  align-items: center;
  padding: var(--space-10);
  opacity: 1;
  transition: opacity 150ms ease-in-out;
  /* purgecss ignore */
}

.product-buy-image-left {
  left: 0;
  padding-left: var(--space-4);
}

.product-buy-image-right {
  right: 0;
  padding-right: var(--space-4);
}

.product-image-fade-enter-active,
.product-image-fade-leave-active {
  transition: opacity 300ms ease-in-out;
}

.product-image-fade-enter-from,
.product-image-fade-leave-to {
  opacity: 0;
}

.zoom-btn {
  display: block;
  height: 40px;
  width: 40px;
  border-radius: 50%;
  border: 1px solid;
  background-color: white;
  border-color: var(--grey-light);
  right: 20px;
  padding: 11px !important;
}

.product-viewer-button-mobile-buy-section {
  display: block;
  width: 100%;
  margin-top: var(--space-6);

  @media (min-width: $mobile-breakpoint) {
    display: none;
  }
}

.product-spinner {
  margin-left: var(--space-5);
}

.loader.tiny {
  width: 15px;
  height: 15px;
}

.notify-container {
  @media (min-width: 768px) and (max-width: calc($desktop-breakpoint - 1px)) {
    display: block;
  }
}
</style>
